import palette from "./palette";

/**
 * Raw visual constants. One definition per value the app renders.
 *
 * Wave 0 populates this with the values already in use so every call-site
 * substitution is identical by construction. Wave 1 changes the values here
 * and nowhere else.
 *
 * `fontSize` and `shadow` maps are added by the tasks that first need them
 * (Task 6 and Task 8) — this task only ships `color`.
 *
 * `color` was Wave 0's promise that its entries are not already reachable
 * through `theme.palette` (e.g. `#222f30` was `secondary.main`,
 * `#FFFFFF`/`#000000` were `common.white`/`common.black`) — those call
 * sites point at the palette instead. The Professional retarget broke that
 * promise for several entries below without removing them: `accentTeal`
 * (`primary.main`), `accentTealDeep` (`primary.dark`), `accentTealBright`
 * (`primary.light`) and `inputBorder` (`grey[400]`) are now byte-identical
 * to a palette slot. This file is Wave B's compatibility layer now, not
 * Wave 0's — each duplicate's own comment says which slot it repeats and
 * that Wave B deletes it; nothing here has been re-verified against that
 * promise since the retarget.
 *
 * A token's string must match its real call site's literal byte-for-byte
 * (spacing included, e.g. `"rgba(0, 0, 0, 0.31)"` vs `"rgba(0,0,0,0.31)"`
 * are different strings to a plain substitution sweep). Only add a value
 * here once you've grepped an actual, non-`theme/` call site for it —
 * values that only appear inside `src/theme/` (e.g. the shadow component
 * colors in `Shadows.tsx`) aren't swept by Wave 0's color/shadow tasks,
 * which both exclude `src/theme/`, so they'd sit here unconsumed.
 */
const tokens = {
  color: {
    // Translucent white for de-emphasised text on a dark surface; already
    // named `ON_DARK_MUTED` at its call site in
    // `src/Pages/Project/Builders/Builder/BuilderHeader/styles.ts`, and
    // also used verbatim in `Loader.stories.tsx:53`.
    onDarkMuted: "rgba(255, 255, 255, 0.7)",

    /**
     * A field's resting border, per the Professional design system. This
     * used to be its own neutral, distinct from `grey[400]`; the retarget
     * landed `grey[400]` on this exact value, so the two are now the same
     * colour (`Override.tsx`'s `MuiOutlinedInput.notchedOutline` points at
     * the palette slot directly for this reason). `divider` (`#DEDEDE`) is
     * still the theme's other, lighter neutral hairline — for cards, not a
     * control's outline. Wave B: delete — use `theme.palette.grey[400]`.
     */
    inputBorder: "#B0ADA4",

    // --- Task 4 (color sweep) additions below. Wave 0 only relocates these;
    // reconciling near-duplicates (several are drift off a palette slot by a
    // channel or two, or the same conceptual value with a case mismatch
    // against the palette) is Wave 1's job. ---

    // RebelOverlay.jsx menu drop-shadow.
    overlayMenuShadow: "rgba(28,38,40,0.32)",

    // react-modal overlays. Three near-identical "secondary at ~90%"
    // backgrounds that drifted apart channel-by-channel under Playful; now
    // unified onto the same value, `secondary.main` (`#082F37`) at 90%.
    // Wave B: delete `hubMemberModalOverlay` and `profileNoPermissionScrim`
    // and point their call sites at `modalOverlay` (or `theme.palette.secondary`).
    modalOverlay: "rgba(8, 47, 55, 0.9)", // src/utils/modalCss.ts
    hubMemberModalOverlay: "rgba(8, 47, 55, 0.9)", // Hubs/Members/HubMember/styles.ts
    profileNoPermissionScrim: "rgba(8, 47, 55, 0.9)", // Pages/Profile/styles.ts

    // Toast.tsx severity icon-strip backgrounds.
    toastWarningStrip: "rgba(0,0,0,0.08)",
    toastFilledStrip: "rgba(255,255,255,0.18)",

    // Loading screen decorations.
    loadingBarShadow: "rgba(28, 38, 40, .2)", // loadingComponent.tsx bar boxShadow
    // Loading.tsx starfield glows are decorative, not part of the neutral or
    // accent ramp — leave as-is, do not "fix" these in a later sweep.
    starGlowWhite: "rgba(255, 255, 255, .8)", // Loading.tsx white starfield
    starGlowBlue: "rgba(190, 200, 255, .9)", // Loading.tsx lone blue star
    starGlowRed: "rgba(255, 200, 200, .8)", // Loading.tsx red starfield

    // Shared low-opacity black used both as a divider background
    // (AppLayout Header) and a card boxShadow (Hubs/Groups GroupCard) —
    // identical bytes at both call sites.
    hairlineShadow: "rgba(28,38,40,0.1)",

    // Hubs/Members `memberColors` values (see the ΔE note in
    // Hubs/Members/styles.ts for how they were chosen). `panelMuted` is an
    // exact case-insensitive match for `grey.100` ("#F4F4F4" in
    // `palette.ts`) but is kept as its own token rather than pointed
    // at the palette, for byte-identical substitution like the other
    // case-variants in this file (see the reportComplete/etc. group below).
    panelMuted: "#F4F4F4", // memberColors.bg; also BuildersQuillForm disabled toolbar bg
    memberBorderLight: "#DEDEDE", // memberColors.grayLight
    memberTextMuted: "#87908F", // memberColors.grayDark
    memberSecondaryLight: "#3E5354", // memberColors.secondaryLight
    memberSecondaryDark: "#16201F", // memberColors.secondaryDark
    // Was Playful `primary.light`. Wave B: delete — use `theme.palette.primary.light`.
    memberAccentLight: "#3AA3AE", // memberColors.accentLight
    // The ramp's meta tone. Wave B: delete — use `theme.palette.grey[500]`.
    memberTextMutedLight: "#87908F", // memberColors.grayDarkLight

    // Member card stack boxShadow, identical in Hubs/Members/styles.ts and
    // Hubs/Members/HubMember/styles.ts.
    cardStackShadow: "rgba(28, 38, 40, 0.3)",

    // Hub reporting charts (Schedule/Builder performance). Same hue family
    // as success/info/warning/error but case-mismatched against the
    // palette's lowercase hex, so not byte-identical substitutions.
    // Was Playful `success.main`. Wave B: delete — use `theme.palette.success.main`.
    reportComplete: "#3E8F77", // Schedule COMPLETED_ON_TIME, Builder Published
    // Already matches Professional's `info.main` (`#2274A5`) — noted, not touched.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    reportUnscheduled: "#2274A5", // Schedule UN_SCHEDULED_COMPLETED
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    reportLate: "#F6B679", // Schedule COMPLETED_LATE
    // Already matches Professional's `error.main` (`#E57373`) — noted, not touched.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    reportIncomplete: "#E57373", // Schedule IN_COMPLETE
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    reportIterations: "#FFE8A9", // Builder Iterations

    // ApexCharts grid line, Hubs/Brain/ActiveUsers.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    chartGridLine: "#f3f3f3",

    // Hubs/Brain Activities per-type chart colors.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityPost: "#F3B357",
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityIdea: "#4A5F61",
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityQuestion: "#4BA1AE",
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityOffer: "#3A74A1",
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityAnnouncement: "#F6A846",
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    activityReplies: "#5EA2D7",

    // MyEvents card boxShadow.
    eventCardShadow: "rgba(28,38,40,0.17)",

    // NoMatch (404) panel background.
    noMatchScrim: "rgba(255, 255, 255, 0.5)",

    // ProjectProfileContainer avatar edit-mode border.
    avatarEditBorder: "rgba(0, 0, 0, 0.5)",

    // Builder Topic card boxShadow, identical in topicSx.ts and
    // BuilderTopicAnswer.tsx.
    topicCardShadow: "rgba(184,184,184,1)",

    // BuilderTopicAnswer "teammates working on this" accent border.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    topicTeammateAccent: "#f18f01",

    // AICritiqueAnalyzing chip pulse keyframes.
    aiCritiquePulseStart: "rgba(0,115,128,0.4)",
    aiCritiquePulseEnd: "rgba(0,115,128,0)",

    // ReviewerChips rank-1 star — a metallic gold, not the chip's own ink.
    // TODO(professional): categorical hue, not specified by the design doc — confirm with design
    champion: "#D4AF37",

    // RebelMap floating panel boxShadow.
    rebelMapShadow: "rgba(232,233,233,1)",

    // BuilderHeader back-button hover wash.
    builderHeaderHoverWash: "rgba(255, 255, 255, 0.08)",

    // Profile rail divider, reused across several rail call sites.
    railDivider: "rgba(255, 255, 255, 0.12)",

    // --- Task 8 (shadow sweep) addition below. A Task 4 escapee: the colors
    // sweep only matched `rgba(`, and this call site uses `rgb(`, so it was
    // missed. Dashboard/Carousel card boxShadow, sm breakpoint. ---
    carouselCardShadow: "rgb(28, 38, 40, 0.08)",

    // --- Task 9 (shade-slot retirement) additions below. These six values
    // lived in MUI's numeric palette shade slots (`primary[50..400]`,
    // `info[50]`), which only worked because `src/mui.d.ts` augmented
    // `PaletteColor`/`SimplePaletteColorOptions` with numeric keys and
    // `Theme-variable.tsx` cast the literals past the checker to smuggle
    // them in. Both the augmentation and the casts are removed along with
    // this sweep. A first pass of the survey (anchored on `primary[50]` /
    // `primary.50` shapes) missed every call site written as
    // `primary?.[50]` — optional chaining before the bracket — so it
    // under-counted `primary[200]`/`primary[400]` as dead; a corrected,
    // fully unanchored survey found real consumers for both. ---

    // The app's broad accent teal: links, icons, borders, chip fills, and
    // form focus washes at ~55 call sites app-wide, plus the global anchor
    // colour and the react-confirm-alert "confirm" button in Override.tsx.
    // Was `info.50` / `info[50]`. `primary.main` — it is the broad accent
    // teal. Wave B: delete — use `theme.palette.primary.main`.
    accentTeal: "#007E8C",
    // A few channels deeper than accentTeal, used near-interchangeably with
    // it across the Builder flow: progress-bar fills and icon/link colours
    // in Project/Builders, Hubs/Brain and Hubs/HubLibrary, the "manage
    // members" / "return to member view" nav-link colour
    // (Hubs/Members/index.tsx, AdminView.tsx), and the BuilderPerformance
    // "Answered" chart status swatch (ColorPalette.ts). Was `primary.50` /
    // `primary[50]`. `primary.dark` — a few channels deeper.
    // Wave B: delete — use `theme.palette.primary.dark`.
    accentTealDeep: "#005E69",
    // A few channels brighter than accentTeal, used for emphasis/selected
    // states: the global `:focus` border in Override.tsx, the Builder Topic
    // "completed" step badge and "selected" side-nav row, the RebelMap
    // control-button fill, the BuilderCard/TopicCard "locked" icon colour,
    // LockedTopic's link colour, and the Members close-button hover. Was
    // `primary.300` / `primary[300]`. `primary.light` — a few channels
    // brighter, for emphasis/selected. Wave B: delete — use `theme.palette.primary.light`.
    accentTealBright: "#3AA3AE",
    // A more muted, grayer teal than the three above. Its only consumer is
    // the mobile HubUserAlert banner's left accent border
    // (Hubs/Brain/HubUserAlert.tsx). Was `primary.200` / `primary[200]`. The
    // doc's own muted, grayer teal (`avatarHues[6]`). Wave B: delete — use
    // `theme.palette.avatarHues[6]`.
    accentTealMuted: "#5C8F95",
    // RebelbaseLogoIcon on the SignIn/SignUp pages — its only consumers,
    // confirming the slot's stale TODO that it exists for the login-page
    // brand logo. Was `primary.100` / `primary[100]`. `brand.logo`.
    // Wave B: delete — use `theme.palette.brand.logo`.
    loginLogoBlue: "#2274A5",
    // The hover-state fill for the BuilderOverview "review/publish" button,
    // whose resting state is `primary.main`. Its only consumer.
    // Was `primary.400` / `primary[400]`. `primary.dark`.
    // Wave B: delete — use `theme.palette.primary.dark`.
    primaryButtonHover: "#005E69",
  },

  // --- Task 6 (font-size sweep) additions below. Every value is stored in
  // the exact unit and form it was found in (bare number or `em`/`rem`
  // string) — no conversion, so a plain name substitution is byte-identical
  // by construction and needs no equivalence proof beyond confirming bare
  // numeric `fontSize` isn't multiplied by any theme factor (see the
  // task-6 report: it isn't, unlike `borderRadius`). Two or more call
  // sites share a name only where they are genuinely the same role/component
  // duplicated (e.g. the parallel HubMember/Members "member card" trees);
  // otherwise each literal is named for its own call site. ---
  fontSize: {
    // Loading spinner's italic caption.
    loadingCaption: "1.2em",
    // CustomImageInput "browse" link.
    imageInputLinkLabel: "0.9em",
    // Toast severity icon.
    toastSeverityIcon: 32,
    // Status dot on a builder topic's section rail — an icon glyph sized down
    // to a bullet, so it is a font size rather than a width.
    builderRailStatusDot: 8,
    // Toast close icon.
    toastCloseIcon: 18,
    // Dashboard CarouselCard due-date icon.
    carouselDueDateIcon: "1rem",
    // Sidebar mobile nav link.
    sidebarMobileMenuLink: "0.8em",
    // Sidebar "more" toggle button.
    sidebarMoreButton: "0.9em",
    // AvatarUploadForm heading ("...Part1").
    avatarUploadHeading: "0.8em",
    // AvatarUploadForm big "Photo" label (h1 override).
    avatarUploadPhotoLabel: "3em",
    // InviteErrorModal table cells (both header and body).
    inviteErrorTableCell: "1.1rem",
    // Required-field asterisk marker; identical in TellUsForm and CreateHub.
    requiredAsterisk: 12,
    // TellUs page heading.
    tellUsHeading: "1.8em",
    // Required-field prompt text; identical in TellUsForm and CreateHub.
    requiredPrompt: 12,
    // TellUs body copy.
    tellUsBody: "1em",
    // Header AvatarGroup avatar-initial text.
    avatarGroupAvatarText: "1rem",
    // LockedTopic message (re-asserted h4 metrics).
    lockedTopicMessage: "1.1em",
    // HubLibrary Builder.tsx topics/sections counts.
    builderLibraryCount: 12,
    // AddGroupMembers type-filter tabs (Hub/Event/Group selectors).
    addGroupMembersFilterTab: "0.8em",
    // AddGroupMembers mobile "Add Managers/Members" heading.
    addGroupMembersMobileHeading: "1.1em",
    // AddGroupMembers section labels (Hub/Event/Group section headers).
    addGroupMembersSectionLabel: "0.8em",
    // EditHubMembership h3 heading.
    membershipEditHeading: "1.6em",
    // EditHubMembership member name.
    memberName: "1.3em",
    // EditHubMembership edit link.
    editLink: "0.8em",
    // HubLibrary BuilderSectionCard "Section" label (body2 override).
    builderSectionCardLabel: 14,
    // Scoring page "%" suffix after a score number; identical for the header
    // score and the total-points readout.
    scoringPercentSuffix: "0.5em",
    // Groups BuilderScheduleManager sortable-item close icon.
    sortableBuilderCloseIcon: 12,
    // Event banner icon.
    eventHeaderBannerIcon: 32,
    // ManageParticipantForm role-chip text (invite row + attendee row +
    // change-role Chip — all the same badge role).
    manageParticipantChipText: "0.7rem",
    // ManageParticipantForm "resend" button.
    manageParticipantResendButton: "0.75rem",
    // GroupCard role badge (Owner/Member ternary).
    groupCardRoleBadge: "0.9em",
    // Admin GroupMembersView role Select.
    memberCardRoleSelect: "0.9rem",
    // Admin GroupMembersView truncated subtext.
    memberCardSubtext: "0.9rem",
    // Activities ActivityMenu option button.
    activityMenuButton: "0.8rem",
    // MyEvents event-card title.
    myEventsCardTitle: "1.6em",
    // MyEvents "Your/Past Events" section heading.
    myEventsSectionHeading: "1.3em",
    // UserSelectionLists select-all / unselect-all link.
    userSelectionListsSelectAllLink: "0.8em",
    // ProjectStatusLegend title.
    projectStatusLegendTitle: "1.1em",
    // Member ProjectsSection clickable project name.
    projectsSectionProjectName: "1.2rem",
    // Admin PerformanceContainer heading.
    performanceContainerHeading: 20,
    // HubMember card role text.
    hubMemberCardRole: "0.9em",
    // Member-card summary title; identical in HubMember and Members trees.
    memberCardSummaryTitle: "0.7em",
    // Member-card summary tag; identical in HubMember and Members trees.
    memberCardTag: "0.8em",
    // Member-card summary info; identical in HubMember and Members trees.
    memberCardSummaryInfo: "0.8em",
    // Members (non-Hub) card name.
    memberCardName: "1.1em",
    // Members (non-Hub) card role text.
    memberCardRole: "0.8em",
    // RebelFormLabel.
    hubFormLabel: "1rem",
    // Members form close button.
    memberFormCloseButton: "1.1em",
    // Members form submit button.
    memberFormSubmitButton: "0.9rem",
    // Brain BrainFilter heading.
    brainFilterHeading: 20,
    // RebelMap embedded button icon.
    rebelMapButtonIcon: 14,
    // RebelMap link label.
    rebelMapLinkLabel: "0.8em",
    // BuilderOverview published-icon (three call sites, one icon/role).
    builderOverviewPublishedIcon: "3em",
    // CreateProject name-field label, base size.
    createProjectNameFieldBase: "1.3em",
    // CreateProject name-field label, >=475px.
    createProjectNameFieldMd: "2em",
    // CreateProject name-field label, >=1050px.
    createProjectNameFieldLg: "3em",
    // CreateProject character-count max-length hint.
    createProjectMaxLength: "0.8em",
    // CreateProject hero title, xs breakpoint.
    createProjectHeroTitleXs: "1.75rem",
    // CreateProject hero title, sm breakpoint.
    createProjectHeroTitleSm: "2.5rem",
    // CreateProject hero title, lg breakpoint.
    createProjectHeroTitleLg: "3rem",
    // Activity Body text, header state.
    activityBodyTextWithHeader: "1.2rem",
    // Activity Body text, no-header state.
    activityBodyTextNoHeader: "1.1rem",
    // Activity Body footer text.
    activityBodyFooterText: "1.05rem",
    // Activity reply text.
    activityReplyText: "0.95rem",
    // IdeaReplies heading.
    ideaRepliesHeading: "1.1rem",
    // IdeaReplies help tooltip.
    ideaRepliesTooltip: "1rem",
    // QuestionReplies heading.
    questionRepliesHeading: "1.4rem",
    // QuestionReplies "voted best answer" label (h4 override).
    questionRepliesBestAnswerLabel: "1rem",
    // QuestionReplies subheading.
    questionRepliesSubheading: "1.1rem",
    // NoMatch "little lost" phrase.
    noMatchLostPhrase: "1.2em",
    // NoMatch sub-text, xs breakpoint (two identical call sites).
    noMatchSubTextXs: "0.9em",
    // NoMatch sub-text, sm breakpoint (two identical call sites).
    noMatchSubTextSm: "1em",
    // SingleActivity back button.
    singleActivityBackButton: "1rem",
    // Builder Topic breadcrumb (re-asserted h2 metrics).
    topicBreadcrumb: "1.5em",
    // Builder Topic breadcrumb, DOWN_SM override.
    topicBreadcrumbMobile: "1.3em",
    // Builder Topic h3-metric heading; identical in BuilderTopicHeader's
    // nameSx and topicSx's titleSx (both re-assert the same _base.scss h3
    // size lost to the Typography h3 variant).
    topicH3Heading: "1.2em",
    // Builder Topic top-links row.
    topicTopLinks: "0.9rem",
    // Builder Topic back button.
    topicBackButton: "0.9em",
    // Builder Topic copyright footer.
    topicCopyrightFooter: "0.5em",
    // BuilderTopicAnswer "last updated" timestamp.
    builderTopicAnswerTimestamp: "0.8rem",
    // Auth SignIn ErrorComponent message — the mockup's "error body" role
    // (Lato 13.5px, was the pre-Playful "0.9em").
    authErrorMessage: "13.5px",
    // Browse Project card location icon.
    browseProjectLocationIcon: 14,
    // Builder Topic side-nav list item.
    topicSideNavItem: "0.9rem",
    // The rich-text editor's toolbar — labels, the heading pill, the word count
    // (Playful states 13px for the pill, 12.5 for the count; one size holds the
    // strip together at the 13 the sheet draws the controls at).
    quillToolbar: "13px",
    // The rich-text editor's writing surface. Reading size, per the sheet's
    // answer step — the same 16.5px the topic body copy takes.
    quillEditorBody: "16.5px",
    // BuildersQuillForm save button.
    quillFormSaveButton: "0.9rem",
    // CreateHub step-two logo-upload label.
    createHubLogoLabel: "0.9em",

    // --- Playful theme wave 0 follow-up: h6 moved back to an ordinary
    // small heading, so the eyebrow role it used to carry now states its
    // own size explicitly at its point of use. ---

    // AuthShell "LOGIN"/"SIGN UP" divider eyebrow label.
    authEyebrow: 12,
    // BuilderCard description ("What need will you meet?"), design-guide spec.
    builderCardDescription: 14,

    // AICritiqueHeader "AI" badge tile, per the Builder Experience mockup.
    aiCritiqueBadge: 16,
    // AICritiqueHeader specialist-count/subtitle line under the panel title.
    aiCritiqueHeaderSubtitle: 13,
    // AICritiqueEmpty dashed-panel "✦" glyph.
    aiCritiqueEmptyIcon: 19,
    // AgentDetail selected specialist's name.
    aiCritiquePersonaName: 20,
    // AgentDetail specialist summary paragraph.
    aiCritiqueSummary: 16,
    // AgentDetail "Do this next" numbered-circle digit.
    aiCritiqueDoThisNextIndex: 12,
    // AgentDetail concern severity badge (CRITICAL/HIGH/…).
    aiCritiqueSeverityBadge: 10,
  },

  // --- Task 7 (spacing sweep) additions below. Every margin/padding/gap
  // literal in the app, named by its own px magnitude rather than a designed
  // scale: an unanchored grep of every spacing prop found the app running a
  // 5px rhythm (10px/5px/20px/15px are its four commonest values, then 4px)
  // — not the 8px grid `theme.spacing` implies. `radii.ts` independently
  // landed on 5 for `shape.borderRadius`, the same rhythm. Converting these
  // onto a real scale is a Wave 1 design decision made with that rhythm
  // known; Wave 0 only relocates the values so that decision has one place
  // to happen. Compound/shorthand strings (e.g. "-67px 0 0") are kept
  // verbatim, never decomposed, since MUI's `sx` shorthands (`m`, `p`) take
  // the full CSS shorthand syntax as one string. ---
  space: {
    // --- Wave 2 (forms) addition. Playful puts a field's label above it in
    // normal flow, 6px away — not MUI's floating label. The shared
    // `src/components/FormInput/Field.tsx` component is the only call site;
    // named for its role rather than folded into the anonymous px6-9 scale
    // below, since those are Wave 0's rote pre-existing-literal relocations
    // and this is a new value introduced by the migration itself. ---
    formLabelGap: "6px",

    px0: "0px",
    px1: "1px",
    px2: "2px",
    px3: "3px",
    px4: "4px",
    px5: "5px",
    px6: "6px",
    px7: "7px",
    px8: "8px",
    px10: "10px",
    px12: "12px",
    px15: "15px",
    px16: "16px",
    px20: "20px",
    px25: "25px",
    px30: "30px",
    // topicSx.ts mainTypeIcon() workshop-icon pl override.
    px32: "32px",
    // topicSx.ts mainTypeIcon() default pl (answer/intro/insprbyte icons).
    px35: "35px",
    px40: "40px",
    px50: "50px",
    px60: "60px",
    /**
     * The room the editor's toolbar strip keeps clear on its right for the word
     * count pinned above it (BuildersQuillForm). Wide enough for "999 words".
     */
    toolbarCountGutter: "88px",
    // Awards awardsStyles.ts place() paddingBottom, badgeThirdSx.
    px68: "68px",
    // Awards awardsStyles.ts place() paddingBottom, badgeSecondSx.
    px95: "95px",
    // HubMember/styles.ts memberModalSideOpenSx mobile mr.
    px130: "130px",
    // Awards awardsStyles.ts place() paddingBottom, badgeFirstSx.
    px155: "155px",
    // MainLayout.jsx Header paddingLeft when the side nav is open
    // (MainLayout.jsx:56) — the Container's own paddingLeft is
    // `sidebarOpenPaddingLeft` below (MainLayout.jsx:67). Both, along with
    // `SidebarWidth` in Theme-variable.tsx, are the SAME sidebar width in
    // three representations, and all three must move together: the sidebar
    // sets its own width from the constant while the main content clears it
    // with these two paddings, so a mismatch slides the content under the
    // rail. Now 264 to match the design system's §08 sidebar.
    // The name is a legacy of the old 224 value; renaming it is a call-site
    // change (MainLayout.jsx) and so is left for whoever owns that file.
    px224: "264px",
    // PlusSign ml/mt (the plus glyph's arm offset).
    pxNeg2: "-2px",
    // AvatarUploadForm heading mb.
    pxNeg10: "-10px",
    // BuilderHeader/styles.ts progress-bar mt.
    pxNeg15: "-15px",
    // BuildersQuillForm save-button wrap mb.
    pxNeg35: "-35px",
    // Members/styles.ts memberCardStackedSx mt (stacked-card pull-up).
    pxNeg290: "-290px",

    // --- Wave 1 (em/rem spacing sweep) additions below. These convert the
    // ~65 sites the px-only Task 7 sweep deliberately left alone (see the
    // SPACING_PX_RE note in scripts/check-visual-constants.js) onto fixed px,
    // rounded to the nearest value that actually exists in the design guide's
    // own spacing scale (32 distinct px values, extracted by frequency from
    // every padding/margin/gap declaration in `RebelBase Design System
    // Playful.dc.html`; it is not a clean 8px ladder). `em` resolves against
    // the *element's own* computed font-size, not the 16px root the way
    // `rem` does, so each em site's px equivalent was derived from its real
    // local font-size context (a Typography variant's theme size, an
    // explicit sibling `fontSize` override, or this app's unstyled default
    // of 16px — confirmed in Override.tsx's MuiCssBaseline block, which sets
    // `html`/`body` `fontFamily` but never `fontSize`) rather than assumed to
    // be 16px uniformly. Full per-site evidence is in the task-10 em/rem
    // sweep report. This is a deliberate rendering change (a redesign wave,
    // not a byte-identical relocation like Task 7) — a few sites shift more
    // than ~2px from their previous em-computed size; those are called out
    // in the report rather than silently rounded away.
    px18: "18px",
    px22: "22px",
    px24: "24px",
    px26: "26px",
    px28: "28px",
    px48: "48px",
    px56: "56px",
    // BuilderTopicHeader.tsx headSx mobile pb, paired with the topicMainSx
    // mobile pull-up below — both are the mobile forced-compositing hack's
    // spacer/pull-up, not rhythm-scale spacing, so they keep their exact
    // converted value rather than rounding onto the small guide scale.
    // Coincidentally the same value as CreateProject/styles.ts
    // titleWrapperSx's desktop-only `mt`, an unrelated one-off.
    px160: "160px",
    // Builder Topic/index.tsx topicMainSx mobile mt (the -8em pull-up under
    // the dark header, paired with px160 above).
    pxNeg128: "-128px",

    // --- Compound/shorthand shapes shared by 2+ call sites. ---
    // Members/HubMember .member-card__summary — rebel/admin/partner wedges.
    memberSummaryOffset118: "-118px 0 0",
    // Members/HubMember .member-card__summary — support/judge wedges.
    memberSummaryOffset67: "-67px 0 0",
    // Members/HubMember .member-card__summary__tag margin.
    memberTagMargin: "0 4px 2px 0",
    // Members/HubMember .member-card__summary__tag padding.
    memberTagPadding: "2px 5px",
    // `inner__layout--whole` mixin padding (CreateProject .cProj,
    // HubMember .member-modal__wrap) — base/>=725px/>=1250px steps.
    innerLayoutPadding: "15px 0",
    innerLayoutPaddingMd: "20px 0",
    innerLayoutPaddingLg: "30px 0",
    // Hub nav-item padding (AdminView tabs, HubLibraryLayout, Builders/index).
    navItemPadding: "12px 16px",
    // Small button padding (CustomImageInput upload, member card forms,
    // BuildersQuillForm save, topicSx mainTypeIcon py, LoadingImage).
    smallButtonPadding: "5px 8px",
    // Tag/empty-state Typography padding (HubProjectCard, HubMembers,
    // PendingInvites).
    tagPadding: "2px 10px",
    // HubMembers/PendingInvites empty-state Typography padding.
    tightRowPadding: "2px 0",
    // Wave 1 em/rem sweep: JudgeJoinModal .judge-welcome__remark and
    // ResourceJoinModal .support-welcome__remark margin — identical "2em 0 0
    // auto" (32px context, no local fontSize override) in both modals.
    welcomeRemarkMargin: "32px 0 0 auto",

    // --- One-off shorthand shapes (single call site each). ---
    // ActivityTile Skeleton.tsx inline Skeleton margin.
    activitySkeletonMargin: "6px 0",
    // Header AvatarGroup padding.
    avatarGroupPadding: "0 5px",
    // Wave 1 em/rem sweep: AvatarUploadForm photo-preview Box margin, base
    // breakpoint. "2em auto 2em" at the default (unstyled) 16px context.
    avatarPhotoPreviewMargin: "32px auto 32px",
    // Wave 1 em/rem sweep: Members/components cardFormButtonSx margin. "1em
    // 0 1em auto" resolved against the button's own 0.9rem (14.4px) font-size
    // (tokens.fontSize.memberFormSubmitButton, via the spread submitButtonSx).
    cardFormButtonMargin: "14px 0 14px auto",
    // Wave 1 em/rem sweep: HubJoinModal/styles.ts joinSectionSx margin. Note:
    // this styles module has no importer anywhere in the app (HubJoinModal's
    // index.tsx builds its own inline sx) — converted for the ratchet's sake
    // like every other site the widened checker would flag, not because it
    // renders. "2em 0" at the default 16px context.
    hubJoinSectionMargin: "32px 0",
    // Wave 1 em/rem sweep: Activities IdeaReplies reply-card CardContent
    // padding. "0 1rem" — rem, so context-independent.
    ideaRepliesCardPadding: "0 16px",
    // Wave 1 em/rem sweep: JudgeJoinModal .judge-welcome__subtitle margin.
    // "4em auto" resolved against the element's own `typography: "h3"` size
    // (19px) — 76px, rounded to the guide's nearest scale value (80); a ~4px
    // shift, called out in the task-10 report.
    judgeWelcomeSubtitleMargin: "80px auto",
    // Wave 1 em/rem sweep: Members/styles.ts membersFlexSx margin. "2em 0" at
    // the default 16px context.
    membersFlexMargin: "32px 0",
    // Wave 1 em/rem sweep: Activities QuestionReplies "Other Answers" <h4>
    // margin. "2rem 0" — rem, context-independent.
    questionRepliesDividerMargin: "32px 0",
    // Wave 1 em/rem sweep: EditHubMembership roleCardGridSx margin. "1em 1em
    // 0 0" at the default 16px context.
    roleCardGridMargin: "16px 16px 0 0",
    // AvatarUploadForm photo-frame breakpoint margin (md).
    avatarUploadPhotoMargin: "2em 1px 2em 4em",
    // Awards data-project-wrap margin.
    awardsProjectWrapMargin: "40px auto",
    // Members/components card-form margin.
    cardFormMargin: "0 10px",
    // CreateHub step-two logo-upload label margin.
    createHubLogoMargin: "3px 0 0",
    // CreateHub .cProj__prompt-equivalent question margin.
    createHubQuestionMargin: "10px 0 25px",
    // CreateProject .cProj__prompt margin.
    createProjectPromptMargin: "15px 0 5px",
    // CreateProject .cProj__prompt margin, >=725px.
    createProjectPromptMarginMd: "40px 0 15px",
    // TellUsForm submit-button margin.
    createProjectSubmitMargin: "2em 0 35px auto",
    // CreateProject .cProj__title margin.
    createProjectTitleMargin: "5px 0",
    // EditHubMembership .tags padding.
    editHubTagPadding: "6px 10px",
    // Header invites-dropdown MuiList-padding.
    headerListPadding: "10px 0",
    // Common ImageUploadModal padding.
    imageUploadModalPadding: "6px 12px",
    // Invitations sender-info box padding.
    invitationsBoxPadding: "10px 15px",
    // Invitations row padding.
    invitationsRowPadding: "8px 0",
    // GroupMembersView InviteMembers button padding.
    inviteButtonPadding: "6.75px 16px",
    // Members .member-modal__header__avatar-equivalent margin.
    memberCardAvatarMargin: "-10px auto 15px",
    // Members .member-card__logo margin.
    memberCardLogoMargin: "0 auto 15px",
    // HubMember .member-card__role margin.
    memberCardRoleMargin: "15px 20px",
    // HubMember .member-card__summary__wrap padding.
    memberCardSummaryWrapPadding: "0 10px 20px",
    // Members .member-card__summary__wrap padding (non-logo variant).
    memberCardSummaryWrapPaddingAlt: "0 10px 10px",
    // Members .member-card__summary — rebel wedge margin.
    memberSummaryOffset109: "-109px 0 0",
    // Members .member-card__summary — general wedge margin.
    memberSummaryOffset145: "-145px 0 0",
    // Members .member-card__summary — admin wedge margin.
    memberSummaryOffset29: "-29px 0 0",
    // Members .member-card__summary — judge wedge margin.
    memberSummaryOffset95: "-95px 0 0",
    // Members .members__wrap.is-member-view padding.
    membersWrapMemberViewPadding: "16px 0",
    // NoMatch scrim margin.
    noMatchScrimMargin: "10px auto",
    // RebelMap embedded-link button padding.
    rebelMapButtonPadding: "3px 7px",
    // RebelMap embedded-link margin.
    rebelMapLinkMargin: "2px 1px",
    // EditHubMembership .role-card margin.
    roleCardMargin: "0 0 10px 0",
    // MainLayout Container paddingLeft when the side nav is open. Must equal
    // `SidebarWidth` and `px224` above — see the note on `px224`.
    sidebarOpenPaddingLeft: "264px !important",
    // MainLayout Container paddingRight (always 0, kept next to the pair above).
    sidebarOpenPaddingRight: "0px !important",
    // MainLayout Header/Container paddingLeft when the side nav is closed
    // (or below the `lg` breakpoint) — clears the sidebar-open gutter above.
    sidebarClosedPadding: "0px !important",
    // TellUsForm form margin, >=600px.
    tellUsFormMarginMd: "30px 50px 30px",
    // TellUsForm form margin, base.
    tellUsFormMarginXs: "30px auto 30px",
    // Builder Topic answer-title margin.
    topicAnswerTitleMargin: "2em 10px 2em",
    // Builder Topic "heads up" teammate-notice margin.
    topicHeadsUpMargin: "0 0 0 10px",
    // Builder Topic question margin.
    topicQuestionMargin: "10px 10px 2em",
    // Builder Topic side-nav aside padding.
    topicSideNavPadding: "10px 10px 30px",
    // Builder Topic "teammates working on this" notice margin.
    topicTeammateMargin: "0 10px 1em",
    // Builder Topic tip-box margin.
    topicTipMargin: "1px 5px",
    // Builder Topic tip-box margin (wide variant).
    topicTipMarginWide: "1em 0px 25px 10px",
    // CustomImageInput upload-button margin.
    uploadButtonMargin: "10px auto 0",
  },

  // --- Task 8 (shadow sweep) additions below. The colors sweep (Task 4)
  // already relocated every shadow's color component into `tokens.color`;
  // this map holds the geometry (offsets/blur/spread) that was left behind,
  // still hardcoded in template literals like
  // `` `9px 17px 20px -7px ${tokens.color.cardStackShadow}` ``.
  //
  // Storage shape: geometry only, never the whole shadow string. Every
  // static-color call site could have taken a whole-string token (the color
  // baked in via a template at definition time), but Toast.tsx's card
  // boxShadow pairs this same geometry with `theme.palette[severity].main`
  // plus a runtime alpha suffix — a color that can't be known until render.
  // One storage shape has to serve both kinds of call site, and only
  // geometry-only does that byte-identically everywhere, so it's used
  // uniformly rather than mixing shapes.
  shadow: {
    // Hubs/Members member-card boxShadow geometry, paired with
    // tokens.color.cardStackShadow. Identical in Members/styles.ts and
    // Members/HubMember/styles.ts.
    memberCardStack: "9px 17px 20px -7px",
    // Hubs/Groups GroupCard boxShadow geometry, paired with
    // tokens.color.hairlineShadow. Identical in both is_active branches.
    groupCardHairline: "8px 8px 8px -3px",
    // RebelMap floating-panel boxShadow geometry, paired with
    // tokens.color.rebelMapShadow.
    rebelMapPanel: "5px 10px 12px 0px",
    // Builder Topic card boxShadow geometry, paired with
    // tokens.color.topicCardShadow. Identical in topicSx.ts and
    // BuilderTopicAnswer.tsx.
    topicCard: "3px 4px 5px 1px",
    // MyEvents card boxShadow geometry, paired with
    // tokens.color.eventCardShadow.
    eventCard: "20px 20px 40px 10px",
    // Loading screen bar boxShadow geometry, paired with
    // tokens.color.loadingBarShadow.
    loadingBar: "1px 1px 0",
    // AICritiqueAnalyzing chip pulse keyframes, paired with
    // tokens.color.aiCritiquePulseStart / aiCritiquePulseEnd. Start and end
    // differ in spread radius (0 vs 6px), so they're kept as two entries.
    aiCritiquePulseStart: "0 0 0 0",
    aiCritiquePulseEnd: "0 0 0 6px",
    // RebelOverlay menu `filter: drop-shadow(...)` geometry, paired with
    // tokens.color.overlayMenuShadow.
    overlayMenu: "0px 2px 8px",
    // Toast.tsx card boxShadow geometry. Paired with a runtime color
    // (`theme.palette[severity].main` plus an alpha suffix), not a token —
    // see the storage-shape note above.
    toastCard: "0 8px 24px",
    // Dashboard/Carousel card boxShadow geometry (sm breakpoint), paired
    // with tokens.color.carouselCardShadow.
    carouselCard: "1px 2px 10px",
    // Loading.tsx starfield boxShadow point lists (WHITE_STARS/RED_STARS).
    // Each list maps an array of `${pos}` coordinates — procedurally-varied
    // placement data, not a reusable design constant, so the positions stay
    // inline. Only the magnitude shared by every entry in a given list is
    // real "geometry" and gets a token.
    starGlowWhiteBlur: ".3vmin", // Loading.tsx randomStarfield() white/blue entries
    starGlowRedBlur: ".2vmin .1vmin", // Loading.tsx randomStarfield() red entries
  },
  /**
   * Wave 2 — design-system sets, from `RebelBase Design System Professional.dc.html`.
   *
   * These deliberately break the Wave 0 rule stated at the top of this file
   * ("only add a value once you've grepped a real non-`theme/` call site").
   * They are not relocated constants; they are the design system's own named
   * sets, added so the theme and its consumers have one place to read them
   * from instead of each call site re-deriving a hex. The neutral ladder in
   * particular is declared exhaustive by the source doc — "nothing outside
   * this set" — which only means anything if the whole set is written down.
   */
  designSystem: {
    /**
     * The neutral ladder, by role. `palette.grey` maps onto this (see
     * `Theme-variable.tsx`); the entries below are the ones with no `grey`
     * slot of their own, plus the two ends of the ramp for completeness.
     */
    ladder: {
      /** Long-form body copy inside cards. Wave B: delete — `grey[800]`. */
      bodyCopy: "#2A3237",
      /** Secondary rows, table cells, icon glyphs. Wave B: delete — `grey[600]`. */
      glyph: "#4A5556",
      /** Disabled text, meta, captions on white. Wave B: delete — `grey[500]`. */
      meta: "#87908F",
      /** Input border, scrollbar thumb, empty step dots. Wave B: delete — `grey[400]`. */
      controlLine: "#B0ADA4",
      /** Breadcrumb slashes, year labels, chevrons. Wave B: keep — no `grey` slot. */
      chevron: "#A19E95",
      /** Divider inside cards and menus — distinct from the card hairline. */
      divider: "#D6D6D6",
      /** Card hairline border. Wave B: delete — `palette.divider`. */
      hairline: "#DEDEDE",
      /** Inset panel / dialog footer. Wave B: delete — `grey[200]`. */
      insetPanel: "#F4F3F0",
      /** Table header, input prefix box. Wave B: delete — `grey[100]`. */
      tableHeader: "#F4F4F4",
      /** Table row hover. Wave B: keep — no `grey` slot. */
      rowHover: "#F8F8F8",
      /** Disabled field fill. Wave B: delete — `grey[300]`. */
      disabledFill: "#E7E5E0",
      /** Marketing / builder band. Wave B: delete — `primary.darker`. */
      deepBand: "#082F37",
      /**
       * The rule one step darker than `divider`: disabled field border,
       * dialog outline, tab-row rule, slider rail. No longer warm, and no
       * longer distinct from `divider` — Professional's ramp has one
       * in-card rule tone. The key stays (renaming it is ~4 call sites and
       * belongs to Wave B).
       */
      ruleWarm: "#D6D6D6",
    },
    /**
     * Status tint pairs. Defined once in `palette.ts` as `palette.tint`; this
     * is the same object, not a copy, so the two cannot drift.
     *
     * Wave B: delete — use `theme.palette.tint`.
     */
    statusTints: palette.tint,
    /**
     * Avatar and stripe hues, in specification order.
     *
     * Wave B: delete — use `theme.palette.avatarHues`.
     */
    avatarHues: palette.avatarHues,
    /** The ground under a Builder topic step's brand artwork. */
    stepKindTile: { bg: "#F4F3F0" },
    /** The label tone on a deep surface — `primary.darker` and `ladder.deepBand`. */
    bandLabel: "#A9CFD5",
    /** The filter-pill (segmented filter) tones that no other component uses. */
    filterPill: {
      /** Hover border on a resting pill. */
      hoverLine: "#C8C6C0",
    },
    toast: {
      shadow: "0 10px 26px rgba(28, 38, 40, 0.24)",
    },
    /** The track card's shadow, between `md` and `lg` on the ramp. */
    trackCardShadow: "0 8px 22px rgba(28, 38, 40, 0.14)",
  },
} as const;

export default tokens;
